(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(root = document) {
    const els = root.querySelectorAll('[data-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window) || reduceMotion) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Header: overlay → solid, hide on scroll down ---------- */
  function initHeader() {
    const wrap = document.querySelector('.site-header-wrap');
    if (!wrap) return;
    const header = wrap.querySelector('.site-header');
    // Announcement bar that sits above the header (if any), for hero height maths
    const announce = document.querySelector('.section-announcement');
    const setHeight = () => {
      document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
      const above = announce && (announce.compareDocumentPosition(wrap) & Node.DOCUMENT_POSITION_FOLLOWING);
      document.documentElement.style.setProperty('--announce-h', above ? `${announce.offsetHeight}px` : '0px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);

    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (wrap.classList.contains('is-overlay')) {
        header.classList.toggle('is-solid', y > 40);
      }
      if (wrap.dataset.hideOnScroll === 'true') {
        wrap.classList.toggle('is-hidden', y > last && y > 200);
      }
      last = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Drawer (mobile menu) ---------- */
  function initDrawers() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      const closer = e.target.closest('[data-drawer-close]');
      if (opener) {
        const d = document.getElementById(opener.dataset.drawerOpen);
        if (d) {
          d.classList.add('is-open');
          d.setAttribute('aria-hidden', 'false');
          opener.setAttribute('aria-expanded', 'true');
          document.body.style.overflow = 'hidden';
          d.querySelector('a, button')?.focus();
        }
      }
      if (closer) {
        const d = closer.closest('.drawer');
        closeDrawer(d);
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') document.querySelectorAll('.drawer.is-open').forEach(closeDrawer);
    });
  }
  function closeDrawer(d) {
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.querySelectorAll(`[data-drawer-open="${d.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }

  /* ---------- Hero parallax ---------- */
  function initParallax() {
    if (reduceMotion) return;
    const items = document.querySelectorAll('.hero__media--parallax');
    if (!items.length) return;
    let ticking = false;
    const update = () => {
      items.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        el.style.setProperty('--py', `${Math.round(-r.top * 0.25)}px`);
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input');
    const min = parseInt(input.min || '0', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || 0) + (btn.dataset.qty === 'plus' ? 1 : -1));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart count ---------- */
  async function refreshCartCount() {
    try {
      const res = await fetch(`${window.theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    } catch (err) { /* noop */ }
  }

  /* ---------- Product form ---------- */
  class ProductForm {
    constructor(root) {
      this.root = root;
      this.form = document.getElementById(root.dataset.formId) || root.querySelector('form[action*="/cart/add"]');
      this.submit = this.form?.querySelector('[type="submit"]');
      this.msg = root.querySelector('[data-form-msg]');
      const json = root.querySelector('[data-product-json]');
      this.product = json ? JSON.parse(json.textContent) : null;
      if (!this.form) return;

      root.addEventListener('change', (e) => {
        if (e.target.closest('[data-option]')) this.onOptionChange();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      if (this.product) this.markAvailability();
    }

    selectedOptions() {
      return [...this.root.querySelectorAll('[data-option]')].map((fs) => {
        const checked = fs.querySelector('input:checked');
        const select = fs.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }

    onOptionChange() {
      if (!this.product) return;
      const opts = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.root.querySelectorAll('[data-option]').forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = opts[i];
      });
      this.markAvailability();
      if (!variant) {
        this.setButton(false, window.theme.strings.unavailable);
        return;
      }
      // Keep every variant input in sync (buy form + installments form)
      this.root.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.setButton(variant.available, variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut);
      this.updatePrice(variant);
      if (variant.featured_media) this.showMedia(variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    markAvailability() {
      const opts = this.selectedOptions();
      this.root.querySelectorAll('[data-option]').forEach((fs, idx) => {
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          const test = [...opts];
          test[idx] = input.value;
          const ok = this.product.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    setButton(enabled, text) {
      if (!this.submit) return;
      this.submit.disabled = !enabled;
      const label = this.submit.querySelector('[data-label]') || this.submit;
      label.textContent = text;
    }

    updatePrice(variant) {
      const el = this.root.querySelector('[data-price]');
      if (!el) return;
      const fmt = (cents) => {
        const tpl = el.dataset.moneyFormat || '${{amount}}';
        const amount = (cents / 100).toFixed(2);
        const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return tpl
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
          .replace(/\{\{\s*amount\s*\}\}/, withCommas);
      };
      el.innerHTML = variant.compare_at_price > variant.price
        ? `<s>${fmt(variant.compare_at_price)}</s> ${fmt(variant.price)}`
        : fmt(variant.price);
    }

    showMedia(mediaId) {
      const target = this.root.querySelector(`[data-media-id="${mediaId}"]`);
      if (!target) return;
      const gallery = target.parentElement;
      if (window.matchMedia('(max-width: 989px)').matches) {
        gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
      } else {
        gallery.prepend(target);
      }
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.submit.disabled) return;
      this.submit.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${window.theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message);
        await refreshCartCount();
        if (this.form.dataset.redirect === 'cart') {
          window.location.href = window.theme.routes.cart_url;
          return;
        }
        const label = this.submit.querySelector('[data-label]');
        const prev = label.textContent;
        label.textContent = window.theme.strings.added;
        setTimeout(() => { label.textContent = prev; }, 2200);
        if (this.msg) this.msg.textContent = '';
      } catch (err) {
        if (this.msg) this.msg.textContent = err.message;
      } finally {
        this.submit.removeAttribute('aria-busy');
      }
    }
  }

  /* ---------- Cart page: auto-submit on qty change ---------- */
  function initCartPage() {
    const form = document.querySelector('[data-cart-form]');
    if (!form) return;
    let t;
    form.addEventListener('change', (e) => {
      if (e.target.name !== 'updates[]') return;
      clearTimeout(t);
      t = setTimeout(() => form.submit(), 500);
    });
  }

  /* ---------- Product recommendations (loaded after page render) ---------- */
  async function initRecommendations(root = document) {
    root.querySelectorAll('[data-recommendations]').forEach(async (el) => {
      if (el.dataset.loaded || !el.dataset.url) return;
      el.dataset.loaded = 'true';
      try {
        const res = await fetch(el.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initReveal(el);
        }
      } catch (err) { /* recommendations are optional */ }
    });
  }

  /* ---------- Address forms: preselect saved country ---------- */
  function initCountrySelects() {
    document.querySelectorAll('select[data-default]').forEach((sel) => {
      if (sel.dataset.default) sel.value = sel.dataset.default;
    });
  }

  function initProducts(root = document) {
    root.querySelectorAll('[data-product-form]').forEach((el) => {
      if (!el._pf) el._pf = new ProductForm(el);
    });
  }

  /* ---------- Sort select on collection pages ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    initDrawers();
    initParallax();
    initProducts();
    initCartPage();
    initRecommendations();
    initCountrySelects();
  });

  /* ---------- Theme editor: re-init sections as they change ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initProducts(e.target);
    initRecommendations(e.target);
    initParallax();
    if (e.target.querySelector('.site-header-wrap')) initHeader();
  });
  document.addEventListener('shopify:section:select', (e) => {
    e.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  });
})();
